import { CeruleanButton, GrayButton } from 'components/Button'
import I18n from 'components/I18n'
import { OverlayLoader } from 'components/Loaders'
import Money from 'components/Money'
import { actions } from 'data'
import { FeeType, TicketFormType } from 'data/types'
import messages from 'lang'
import React, { useContext, useEffect } from 'react'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import { useDispatch } from 'react-redux'
import { MarketDataContext } from 'scenes/MarketData/Context'
import { getBest, getPlaceholderPrice } from 'scenes/MarketData/utils'
import webSocket from 'services/api/webSocket'
import { toMaxPrecision } from 'utils'

import calculateFee from '../Ticket/Form/calculateFee'
import { PropsType as _P } from './index'
import OrderDetails from './OrderDetails'
import { Footer, getOrderTypeString, Header, HeaderItem, TIF_TEXT_MAP, Wrapper } from './utils'

type PropsType = _P & WrappedComponentProps

type FeeProps = {
  fees?: FeeType
  order: TicketFormType
}
const Fees = ({ fees, order }: FeeProps) => {
  if (!fees) return null
  return (
    <>
      <I18n data={messages.fee_percentage_maker} value={fees.makerFee / 100} />
      {order.liquidity !== true && (
        <>
          <br />
          <I18n data={messages.fee_percentage_taker} value={fees.takerFee / 100} />
        </>
      )}
      {order.marginOrder && (
        <>
          <br />
          <I18n data={messages.fee_percentage_margin_interest} rate='0.02%' time='4h' />
        </>
      )}
    </>
  )
}

const OrderConfirmation = ({
  counterCurrencySym,
  fees,
  intl,
  marginDetails,
  order,
  place,
  precision,
  symbol,
  ticker,
  userFees
}: PropsType) => {
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(actions.auth.getUserFees())
  }, [])

  const { market } = useContext(MarketDataContext)
  const best = getBest(market)
  const price = getPlaceholderPrice(ticker, symbol, order, best)
  const fee = calculateFee(fees, order.type, order.liquidity)
  const numQuantity = parseFloat(order.quantity ?? '0')
  const estimatedTotal = toMaxPrecision(
    order.side === 'buy' ? numQuantity * (1 + fee) * price : numQuantity * (1 - fee) * price,
    precision.price
  )

  if (!userFees) return <OverlayLoader />

  return (
    <Wrapper>
      <Header>
        <HeaderItem side={order.side}>
          <span>{symbol}</span>
          <span>
            {order.side} {order.type}
          </span>
        </HeaderItem>
        {order.marginOrder ? (
          <HeaderItem side={order.side}>
            <span>
              <I18n data={messages.margin_leverage} />
            </span>
            <span>{order.leverageRatio}X</span>
          </HeaderItem>
        ) : (
          <HeaderItem side={order.side}>
            <span>{intl.formatMessage(messages.title_order_confirmation)}</span>
          </HeaderItem>
        )}
      </Header>
      <OrderDetails
        amount={parseFloat(order.quantity ?? '0')}
        estimatedTotal={<Money amount={estimatedTotal} currency={counterCurrencySym} />}
        fees={<Fees fees={userFees[symbol].active} order={order} />}
        goodToDate={order.tif === webSocket.order.tif.GTD ? order.goodToDate : undefined}
        limitPrice={
          order.type === webSocket.order.type.LIMIT ||
          order.type === webSocket.order.type.STOP_LIMIT
            ? parseFloat(order.limitPrice ?? '0')
            : undefined
        }
        liquidationPrice={
          order.marginOrder && marginDetails ? marginDetails.liquidationPrice : undefined
        }
        liquidity={order.type === webSocket.order.type.LIMIT ? order.liquidity : undefined}
        markPrice={order.marginOrder && marginDetails ? marginDetails.markPrice : undefined}
        minAmount={order.tif === webSocket.order.tif.IOC ? order.minimumQuantity : undefined}
        positionMargin={
          order.marginOrder && marginDetails ? marginDetails.collateralAmount : undefined
        }
        positionMarginCurrency={
          order.marginOrder && marginDetails ? order.collateralCurrency : undefined
        }
        side={order.side}
        stopLoss={order.marginOrder ? parseFloat(order.stopLoss ?? '0') : undefined}
        symbol={symbol}
        takeProfit={order.marginOrder ? order.takeProfit : undefined}
        tif={order.type !== webSocket.order.type.MARKET ? TIF_TEXT_MAP[order.tif] : undefined}
        triggerPrice={
          order.type === webSocket.order.type.STOP_LIMIT || order.type === webSocket.order.type.STOP
            ? order.triggerPrice
            : undefined
        }
        type={getOrderTypeString(order.type)}
      />
      <Footer>
        <GrayButton
          e2e='confirmationCancelOrderButton'
          onClick={() => dispatch(actions.app.hideModal('ORDER_CONFIRMATION'))}
        >
          <I18n data={messages.order_confirmation_cancel} />
        </GrayButton>
        <CeruleanButton
          e2e='confirmationSubmitOrderButton'
          onClick={() => {
            place({
              amount: order.quantity,
              collateralAmount:
                order.marginOrder && marginDetails ? marginDetails.collateralAmount : undefined,
              collateralCurrency:
                order.marginOrder && marginDetails ? order.collateralCurrency : undefined,
              leverageRatio: order.leverageRatio,
              margin: order.marginOrder,
              pair: symbol,
              price: order.limitPrice,
              side: order.side,
              stopPrice: order.triggerPrice,
              tif: order.tif,
              type: order.type
            })
          }}
        >
          <I18n data={messages.order_confirmation_submit} />
        </CeruleanButton>
      </Footer>
    </Wrapper>
  )
}

export default injectIntl(OrderConfirmation)
